import { Instagram, Facebook, ArrowUpRight, Star } from "lucide-react";
import { HornsMark } from "./Header";
import { BISTECCHINO_URL, FACEBOOK_URL, GOOGLE_MAPS_URL, INSTAGRAM_URL, LIKEFOLLONICA_URL, PHONE_DISPLAY, PHONE_TEL, gaEvent } from "@/data/content";

const Footer = () => (
  <footer data-testid="footer-section" className="relative border-t border-[#C5A059]/15">
    <a
      data-testid="john-family-banner"
      href={BISTECCHINO_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between gap-4 bg-[#7A1C2E] hover:bg-[#9E2A3F] transition-colors duration-300 px-5 sm:px-8 py-6"
    >
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <HornsMark className="w-8 h-6 text-[#F7F4EE]/80" />
          <div>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-[#F7F4EE]/70">
              Fa parte della famiglia John
            </span>
            <span className="block font-display text-lg sm:text-xl text-[#F7F4EE]">
              Scopri anche John Bistecchino
            </span>
          </div>
        </div>
        <ArrowUpRight
          size={24}
          className="text-[#F7F4EE] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
          data-testid="john-bistecchino-link"
        />
      </div>
    </a>

    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-3 gap-10 items-start">
      <div>
        <img
          src="/img/logo.png"
          alt="John Tartarino — Ristorante di carne e cucina maremmana, Follonica"
          className="h-14 w-auto"
        />
        <p className="mt-4 text-sm text-[#C5BCAD]/80 leading-relaxed max-w-xs">
          Ristorante di carne e cucina maremmana nel centro storico di Follonica.
          Tartare, stracotte, brace e una carta dei vini ampia.
        </p>
      </div>

      <div>
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">Contatti</span>
        <a
          href={PHONE_TEL}
          onClick={() => gaEvent("click_telefono", { posizione: "footer" })}
          className="mt-3 block font-display text-lg text-[#F7F4EE] hover:text-[#C5A059] transition-colors"
        >
          {PHONE_DISPLAY}
        </a>
        <a
          data-testid="footer-email-link"
          href="mailto:info@johntartarino.it"
          onClick={() => gaEvent("click_email", { posizione: "footer" })}
          className="mt-1.5 block text-sm text-[#C5BCAD] hover:text-[#C5A059] transition-colors"
        >
          info@johntartarino.it
        </a>
        <p className="mt-2 text-sm text-[#C5BCAD]/80">
          Via Martiri della Niccioleta 13 — 58022 Follonica (GR)
        </p>
      </div>

      <div>
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#C5BCAD]">Seguici</span>
        <div className="mt-4 flex items-center gap-3">
          <a
            data-testid="social-instagram"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="w-11 h-11 flex items-center justify-center border border-[#F7F4EE]/15 text-[#C5BCAD] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            <Instagram size={18} strokeWidth={1.5} />
          </a>
          <a
            data-testid="social-facebook"
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="w-11 h-11 flex items-center justify-center border border-[#F7F4EE]/15 text-[#C5BCAD] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            <Facebook size={18} strokeWidth={1.5} />
          </a>
          <a
            data-testid="social-google"
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => gaEvent("click_mappa", { posizione: "footer" })}
            aria-label="Scheda Google"
            className="w-11 h-11 flex items-center justify-center border border-[#F7F4EE]/15 text-[#C5BCAD] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            <Star size={18} strokeWidth={1.5} />
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-[#F7F4EE]/8 py-6 px-5 text-center text-xs text-[#C5BCAD]/60 tracking-wide leading-relaxed">
      <p className="max-w-2xl mx-auto">
        Cerchi la fiorentina alla griglia, la costata, la carne al peso? Quello è il
        mestiere del{" "}
        <a
          data-testid="footer-bistecchino-note"
          href="http://www.johnbistecchino.it/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#C5A059] hover:text-[#F7F4EE] transition-colors"
        >
          John Bistecchino
        </a>
        , Via Albereta 9.
      </p>
    </div>

    <div className="border-t border-[#F7F4EE]/8 py-6 px-5 text-center text-xs text-[#C5BCAD]/50 tracking-wide">
      © {new Date().getFullYear()} John Tartarino — Follonica, Toscana. Tutti i diritti riservati.
      <span className="mx-2 text-[#C5A059]/40">·</span>
      Con il territorio:{" "}
      <a
        data-testid="likefollonica-link"
        href={LIKEFOLLONICA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#C5BCAD]/70 hover:text-[#C5A059] transition-colors"
      >
        Like Follonica
      </a>
      <span className="mx-2 text-[#C5A059]/40">·</span>
      <button
        data-testid="privacy-policy-link"
        onClick={() => window.dispatchEvent(new Event("jt-open-privacy"))}
        className="text-[#C5BCAD]/70 hover:text-[#C5A059] transition-colors"
      >
        Privacy &amp; Cookie Policy
      </button>
      <span className="mx-2 text-[#C5A059]/40">·</span>
      <button
        data-testid="cookie-prefs-link"
        onClick={() => window.dispatchEvent(new Event("jt-cookie-prefs"))}
        className="text-[#C5BCAD]/70 hover:text-[#C5A059] transition-colors"
      >
        Preferenze cookie
      </button>
    </div>
  </footer>
);

export default Footer;
